"use client";

import React, { useEffect, useRef, useState } from "react";
import { Calendar, DollarSign, ShieldCheck, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  icon: ShieldCheck,
  title: "Avoinna kellon ympäri\xA0",
  description: "Voit hoitaa varastointiasiat siinä aikataulussa kun ne sinulle sopivat."
}, {
  icon: Calendar,
  title: "Säästä aikaa\xA0",
  description: "Vuokraa varasto toistaiseksi. Ei pitkiä sitoutumisia tai monimutkaisia sopimuksia."
}, {
  icon: DollarSign,
  title: "Joka 7 kk ilmainen",
  description: "Haluamme antaa uusille ja vanhoille asiakkaillemme puolen vuoden jälkeen ilmaisen kuukauden, eli 7kk on maksuton!"
}, {
  icon: MapPin,
  title: "Sijainti",
  description: "Helppo pääsy varastollesi Mikkelissä sujuvien liikenneyhteyksien varrella."
}];
function PillarCard({
  pillar,
  index
}: {
  pillar: (typeof pillars)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("flex flex-col items-center text-center p-6 rounded-2xl transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", "hover:bg-card border border-border hover:border-secondary/20 shadow-sm hover:shadow-lg")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <div className="mb-6 p-4 bg-secondary/10 rounded-lg text-secondary ring-1 ring-secondary/20 shadow-md">
        <pillar.icon className="w-8 h-8" strokeWidth={1.5} />
      </div>
      <h3 className="font-default text-xl font-semibold mb-3 tracking-tight text-primary">
        {pillar.title}
      </h3>
      <p className="text-muted-foreground font-serif leading-relaxed">
        {pillar.description}
      </p>
    </div>;
}
export default function TheSBudadyAdvantage() {
  return <section data-section-id="227975" id="miksi-valita-varastomikkeli" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-0 w-full overflow-hidden flex justify-center" aria-hidden="true">
        <span className="text-[15vw] md:text-[12vw] font-bold text-primary/[0.02] whitespace-nowrap tracking-tighter font-default">
          Yksinkertainen ja nopea
        </span>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 md:mb-24">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-primary">
            Miksi valita Varastomikkeli?
          </h2>
          <div className="w-24 h-1 bg-secondary mx-auto rounded-lg opacity-100" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {pillars.map((pillar, i) => <PillarCard key={i} pillar={pillar} index={i} />)}
        </div>
      </div>
    </section>;
}